import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../hooks/usePermissions";
import { ThemeToggle } from "./ThemeToggle";
import { TajerLogoFull, TajerLogoIcon } from "./ui/TajerLogo";
import GlobalSearch from "./GlobalSearch";
import { NotificationCenter } from "./common";
import { FloatingActionButton } from "./FloatingActionButton";
import BranchSwitcher from "./branches/BranchSwitcher";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { cn } from "../lib/utils";
import { isSafeUrl } from "../lib/security";
import { useDensity } from "../design-system";
import {
  LayoutDashboard,
  Briefcase,
  Package,
  Network,
  Globe,
  Wallet,
  Users,
  Settings,
  Brain,
  Bell,
  Target,
  FileText,
  Store,
  CreditCard,
  ShoppingBag,
  Warehouse,
  Building2,
  ShoppingCart,
  Factory,
  ArrowUpDown,
  Calculator,
  BarChart3,
  FileBarChart,
  DollarSign,
  Calendar,
  Clock,
  Timer,
  Award,
  Layers,
  Shield,
  ShieldCheck,
  Zap,
  CheckCircle2,
  ClipboardList,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
  UserCircle,
  Menu,
  X,
  TrendingUp,
  FolderKanban,
  Cpu,
  AlertTriangle,
  Leaf,
  MessageSquare,
  Sparkles,
  Banknote,
  Ship,
  LogIn,
  LogOut as LogOutIcon,
  UserCheck,
  BookOpen,
  Receipt,
  Boxes,
  Trash2,
  Repeat,
} from "lucide-react";

// ─── Constants ────────────────────────────────────────────────────────────────
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API_URL = `${BACKEND_URL}/api`;

const ROLE_DISPLAY = {
  admin: "مدير النظام",
  manager: "مدير",
  accountant: "محاسب",
  sales: "مبيعات",
  warehouse: "مستودعات",
  hr: "موارد بشرية",
  viewer: "مشاهد",
  user: "مستخدم",
};

// localStorage keys
const LS_COLLAPSED = "tajer-sidebar-collapsed";

const readLS = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    if (v === null) return fallback;
    const parsed = JSON.parse(v);
    return typeof fallback === "boolean" && typeof parsed !== "boolean"
      ? fallback
      : parsed;
  } catch {
    return fallback;
  }
};
const writeLS = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

// ─── Sidebar Data ─────────────────────────────────────────────────────────────
const SIDEBAR_GROUPS = [
  // ── 1. لوحة القيادة ────────────────────────────────────────────────────────
  {
    id: "dashboard",
    label: "لوحة القيادة",
    icon: LayoutDashboard,
    items: [
      { path: "/dashboard", label: "لوحة التحكم", icon: Sparkles },
      { path: "/branches", label: "الفروع", icon: Store },
      // /ai-assistant lives solely under "منصة القيادة الذكية والحوكمة" now
      // (GOVAI-002B). Route + page are unchanged; only the duplicate nav entry
      // was removed.
      { path: "/notifications", label: "الإشعارات", icon: Bell },
      {
        path: "/operations-readiness-index",
        label: "جاهزية الربط التشغيلي",
        icon: Layers,
        spec: true,
      },
    ],
  },

  // ── منصة القيادة الذكية والحوكمة ────────────────────────────────────────────
  // Tajer Intelligent Governance Platform (GOVAI-001/002/002B). This group is
  // the SINGLE Sidebar owner of the AI / governance / decision / risk
  // surfaces — they no longer appear as standalone duplicates in other groups.
  //
  //   • No old route was removed and no page file was deleted — only the
  //     duplicate nav entries elsewhere were dropped. Deep links keep working.
  //   • The route set below must stay a subset of the registry
  //     (intelligentGovernanceRegistry.js → GOVERNANCE_ROUTES); the
  //     Layout.governance-consolidation test enforces both that subset
  //     relationship and that no route appears in more than one Sidebar group.
  //   • The Decision Signal Registry (GOVAI-002) is reached as the "سجل إشارات
  //     القرار" tab INSIDE /intelligent-governance, so it is intentionally not
  //     a second nav entry pointing at the same route.
  //   • /trade-dashboard and /compliance keep their operational home in the
  //     "التجارة الدولية" group (their domain dashboard + trade-compliance
  //     pages); the platform PAGE still aggregates them via the registry as
  //     "مراقبة السوق والتجارة" / "الحوكمة والامتثال".
  {
    id: "intelligent-governance",
    label: "منصة القيادة الذكية والحوكمة",
    icon: ShieldCheck,
    items: [
      {
        path: "/intelligent-governance",
        label: "لوحة القيادة الذكية",
        icon: ShieldCheck,
      },
      { path: "/ai-assistant", label: "المساعد الذكي", icon: Brain },
      {
        path: "/zero-ui",
        label: "Zero UI — الواجهة الذكية",
        icon: MessageSquare,
      },
      {
        path: "/neural-cloud",
        label: "Neural Cloud",
        icon: Network,
        spec: true,
      },
      { path: "/digital-twin", label: "التوأم الرقمي", icon: Cpu, spec: true },
      {
        path: "/fraud-watchdog",
        label: "مراقبة الاحتيال",
        icon: AlertTriangle,
      },
      {
        path: "/admin/observability",
        label: "مركز المراقبة الإداري",
        icon: Shield,
      },
      { path: "/automation", label: "سير العمل التلقائي", icon: Zap },
      { path: "/automation/builder", label: "منشئ الأتمتة", icon: Settings },
      { path: "/hr-intelligence", label: "ذكاء الموارد البشرية", icon: Leaf },
    ],
  },

  // ── 2. المبيعات والعملاء ───────────────────────────────────────────────────
  {
    id: "sales",
    label: "المبيعات والعملاء",
    icon: Briefcase,
    items: [
      { path: "/crm", label: "إدارة العملاء CRM", icon: Target },
      { path: "/customers", label: "العملاء", icon: Users },
      { path: "/quotations", label: "عروض الأسعار", icon: ClipboardList },
      { path: "/invoices", label: "الفواتير", icon: FileText },
      { path: "/recurring-invoices", label: "فواتير متكررة", icon: Repeat },
      { path: "/pos", label: "نقطة البيع", icon: Store },
      { path: "/pos/sessions", label: "إدارة الجلسات", icon: ClipboardList },
      { path: "/pos/reports", label: "تقارير POS", icon: BarChart3 },
      {
        path: "/pos/operations-control-center",
        label: "مركز عمليات نقطة البيع",
        icon: ShieldCheck,
        spec: true,
      },
      { path: "/promotions", label: "العروض الترويجية", icon: Sparkles },
      { path: "/loyalty", label: "برنامج الولاء", icon: Award },
      { path: "/ecommerce", label: "المتجر الإلكتروني", icon: ShoppingBag },
      { path: "/sales", label: "المبيعات", icon: TrendingUp },
    ],
  },

  // ── 3. العمليات والمخزون ───────────────────────────────────────────────────
  {
    id: "operations",
    label: "العمليات والمخزون",
    icon: Package,
    items: [
      { path: "/inventory", label: "نظرة عامة على المخزون", icon: Boxes },
      { path: "/products", label: "المنتجات", icon: Package },
      { path: "/stock-moves", label: "حركات المخزون", icon: ArrowUpDown },
      { path: "/warehouses", label: "المستودعات", icon: Warehouse },
      { path: "/suppliers", label: "الموردين", icon: Building2 },
      { path: "/purchases", label: "المشتريات", icon: ShoppingBag },
      { path: "/purchase-orders", label: "أوامر الشراء", icon: ShoppingCart },
      { path: "/manufacturing", label: "التصنيع", icon: Factory },
    ],
  },

  // ── 4. التجارة الدولية ─────────────────────────────────────────────────────
  {
    id: "trade",
    label: "التجارة الدولية",
    icon: Globe,
    items: [
      { path: "/trade-dashboard", label: "لوحة التجارة الدولية", icon: Globe },
      { path: "/shipments", label: "الشحنات", icon: Ship },
      { path: "/customs", label: "التخليص الجمركي", icon: FileText },
      { path: "/free-zones", label: "المناطق الحرة", icon: Warehouse },
      { path: "/supply-chain", label: "سلاسل الإمداد", icon: TrendingUp },
      { path: "/re-export", label: "إعادة التصدير", icon: Zap },
      { path: "/compliance", label: "الامتثال التجاري", icon: CheckCircle2 },
      { path: "/risk-assessment", label: "تقييم المخاطر", icon: AlertTriangle },
      { path: "/cost-optimization", label: "تحسين التكاليف", icon: DollarSign },
    ],
  },

  // ── 5. المالية والمحاسبة ───────────────────────────────────────────────────
  {
    id: "finance",
    label: "المالية والمحاسبة",
    icon: Wallet,
    items: [
      { path: "/accounting", label: "المحاسبة", icon: Calculator },
      {
        path: "/accounting/close-dashboard",
        label: "جاهزية إغلاق الفترة",
        icon: ClipboardList,
        spec: true,
      },
      { path: "/journal-entries", label: "قيود اليومية", icon: BookOpen },
      {
        path: "/financial-reports",
        label: "التقارير المالية",
        icon: FileBarChart,
      },
      { path: "/payments", label: "إدارة المدفوعات", icon: Wallet },
      { path: "/banks", label: "الحسابات البنكية", icon: Building2 },
      {
        path: "/banks/reconciliation",
        label: "المطابقة البنكية",
        icon: ArrowUpDown,
      },
      { path: "/expenses", label: "المصروفات", icon: DollarSign },
      {
        path: "/vat-return",
        label: "إقرار ضريبة القيمة المضافة",
        icon: FileBarChart,
      },
      { path: "/settings/vat", label: "إعدادات الضريبة (VAT)", icon: Receipt },
      { path: "/zatca", label: "ZATCA الفوترة", icon: Receipt },
    ],
  },

  // ── 6. رأس المال البشري ────────────────────────────────────────────────────
  {
    id: "hr",
    label: "رأس المال البشري",
    icon: Users,
    subgroups: [
      {
        id: "hr-core",
        label: "الموارد البشرية",
        items: [
          { path: "/employees", label: "الموظفين", icon: UserCheck },
          {
            path: "/hr/departments",
            label: "الأقسام والمناصب",
            icon: Building2,
          },
          { path: "/hr/overtime", label: "الساعات الإضافية", icon: Clock },
          { path: "/hr/attendance", label: "الحضور والانصراف", icon: Clock },
          { path: "/hr/leaves", label: "الإجازات", icon: Calendar },
          {
            path: "/hr/leave-calendar",
            label: "تقويم الإجازات",
            icon: Calendar,
          },
          { path: "/hr/timesheets", label: "Timesheets", icon: Timer },
          { path: "/hr/performance", label: "إدارة الأداء", icon: Award },
          // /hr-intelligence now lives solely under "منصة القيادة الذكية
          // والحوكمة" (GOVAI-002B). Route + page unchanged.
        ],
      },
      {
        id: "hr-payroll",
        label: "الرواتب",
        items: [
          { path: "/payroll/runs", label: "مسيرات الرواتب", icon: DollarSign },
          { path: "/payroll/components", label: "مكونات الراتب", icon: Layers },
          { path: "/payroll/loans", label: "القروض والسلف", icon: Calculator },
          {
            path: "/payroll/gratuity",
            label: "مكافأة نهاية الخدمة",
            icon: Award,
          },
          { path: "/payroll/wps", label: "WPS حماية الأجور", icon: Shield },
          { path: "/portal/my-payslips", label: "قسائم راتبي", icon: FileText },
        ],
      },
      {
        id: "hr-approvals",
        label: "الموافقات",
        items: [
          {
            path: "/approvals/inbox",
            label: "صندوق الموافقات",
            icon: CheckCircle2,
          },
          {
            path: "/approvals/my-requests",
            label: "طلباتي للموافقة",
            icon: ClipboardList,
          },
          {
            path: "/approvals/chains",
            label: "سلاسل الموافقة",
            icon: Settings,
          },
        ],
      },
    ],
  },

  // ── 7. المنصة والإعدادات ───────────────────────────────────────────────────
  {
    id: "platform",
    label: "المنصة والإعدادات",
    icon: Settings,
    subgroups: [
      // The "الأتمتة" subgroup (/automation + /automation/builder) was removed
      // here in GOVAI-002B; both tools now live solely under "منصة القيادة
      // الذكية والحوكمة". Routes + pages are unchanged.
      {
        id: "platform-reports",
        label: "التقارير",
        items: [
          { path: "/reports", label: "التقارير", icon: BarChart3 },
          { path: "/reports/builder", label: "منشئ التقارير", icon: BarChart3 },
        ],
      },
      {
        id: "platform-settings",
        label: "الإعدادات",
        items: [
          {
            path: "/settings/custom-fields",
            label: "الحقول المخصصة",
            icon: Layers,
          },
          {
            path: "/settings/notification-preferences",
            label: "تفضيلات الإشعارات",
            icon: Bell,
          },
          { path: "/settings/audit-log", label: "سجل المراجعة", icon: Shield },
          { path: "/users", label: "إدارة المستخدمين", icon: Shield },
          { path: "/trash", label: "سلة المحذوفات", icon: Trash2 },
          { path: "/settings", label: "إعدادات الشركة", icon: Settings },
        ],
      },
      {
        id: "platform-advanced",
        label: "وحدات متقدمة",
        // /admin/observability, /neural-cloud, /digital-twin, /fraud-watchdog
        // and /zero-ui were removed from this subgroup in GOVAI-002B; they now
        // live solely under "منصة القيادة الذكية والحوكمة". Routes + pages are
        // unchanged. /projects is not a governance tool and stays here.
        items: [
          { path: "/projects", label: "إدارة المشاريع", icon: FolderKanban },
        ],
      },
    ],
  },
];

// ─── Permission rules per nav path ────────────────────────────────────────────
// Frontend filtering is UX-only; backend enforcement is the source of truth.
const NAV_PERMISSION_RULES = [
  { prefixes: ["/dashboard", "/notifications"], resource: "dashboard" },
  // Intelligent Governance Platform landing — read-only aggregation surface.
  // Classified as dashboard:read (same as the executive dashboard) so it never
  // expands a sensitive permission; the individual tools keep their own rules.
  { prefixes: ["/intelligent-governance"], resource: "dashboard" },
  { prefixes: ["/branches"], resource: "settings" },
  { prefixes: ["/ai-assistant", "/zero-ui"], resource: "ai_assistant" },
  { prefixes: ["/crm"], resource: "crm" },
  { prefixes: ["/customers"], resource: "customers" },
  { prefixes: ["/quotations"], resource: "quotations" },
  { prefixes: ["/invoices", "/recurring-invoices"], resource: "invoices" },
  {
    prefixes: ["/pos", "/promotions", "/loyalty", "/ecommerce", "/sales"],
    resource: "sales",
  },
  { prefixes: ["/inventory", "/products"], resource: "products" },
  { prefixes: ["/stock-moves", "/warehouses"], resource: "warehouses" },
  { prefixes: ["/suppliers"], resource: "suppliers" },
  { prefixes: ["/purchases", "/purchase-orders"], resource: "purchases" },
  { prefixes: ["/manufacturing"], resource: "manufacturing" },
  {
    prefixes: [
      "/trade-dashboard",
      "/shipments",
      "/customs",
      "/free-zones",
      "/supply-chain",
      "/re-export",
      "/compliance",
      "/risk-assessment",
      "/cost-optimization",
    ],
    resource: "reports",
  },
  {
    prefixes: [
      "/accounting",
      "/journal-entries",
      "/financial-reports",
      "/payments",
      "/expenses",
      "/zatca",
      "/vat-return",
    ],
    resource: "accounting",
  },
  { prefixes: ["/banks"], resource: "banking", action: "read_sensitive" },
  { prefixes: ["/employees", "/hr"], resource: "employees" },
  { prefixes: ["/payroll", "/portal/my-payslips"], resource: "payroll" },
  { prefixes: ["/approvals"], resource: "payroll", action: "approve" },
  { prefixes: ["/automation"], resource: "workflows" },
  { prefixes: ["/reports"], resource: "reports" },
  {
    prefixes: ["/settings/audit-log", "/users", "/trash", "/settings"],
    resource: "settings",
  },
  { prefixes: ["/projects"], resource: "projects" },
  { prefixes: ["/digital-twin", "/fraud-watchdog"], resource: "strategy" },
  // Admin observability surfaces (cascade failures, perf, feature usage,
  // unit economics, retention, plans, health) — gated on settings:read.
  {
    prefixes: [
      "/admin/observability",
      "/neural-cloud",
      "/admin/cascades",
      "/admin/perf",
      "/admin/feature-usage",
      "/admin/unit-economics",
      "/admin/retention",
      "/admin/plans",
      "/admin/health",
    ],
    resource: "settings",
  },
  // Strategic surfaces (executive tower, north star, strategy map,
  // sustainability) — gated on reports:read.
  {
    prefixes: [
      "/admin/executive-tower",
      "/admin/north-star",
      "/admin/sustainability",
      "/strategy/objectives",
      "/strategy/tasks",
      "/strategy/map",
      "/strategic-initiatives",
    ],
    resource: "reports",
  },
  // Cross-module operations readiness index — read-only spec surface in the
  // dashboard group. Previously matched no rule and relied on the permissive
  // fallback; classified explicitly here (dashboard:read, unchanged effective
  // visibility) so the global fallback can deny by default.
  { prefixes: ["/operations-readiness-index"], resource: "dashboard" },
];

// Safe-deny fallback: a nav path that matches no rule resolves to a sentinel
// resource that NO role grants, so non-admin users never see an unclassified
// item by accident (admins still see it via the '*' wildcard). This forces
// every new nav path to be explicitly classified above or to carry an
// `item.permission` override. Backend remains the source of truth for access.
const UNMAPPED_NAV_PERMISSION = { resource: "__unmapped__", action: "read" };

const getNavPermission = (path) =>
  NAV_PERMISSION_RULES.find((rule) =>
    rule.prefixes.some((prefix) => path.startsWith(prefix)),
  ) || UNMAPPED_NAV_PERMISSION;

const canSeeNavItem = (item, can) => {
  const rule = item.permission || getNavPermission(item.path);
  return can(rule.resource, rule.action || "read");
};

const filterSidebarGroups = (groups, can) =>
  groups
    .map((group) => {
      if (group.subgroups) {
        const subgroups = group.subgroups
          .map((sg) => ({
            ...sg,
            items: sg.items.filter((item) => canSeeNavItem(item, can)),
          }))
          .filter((sg) => sg.items.length > 0);
        return { ...group, subgroups };
      }
      return {
        ...group,
        items: (group.items || []).filter((item) => canSeeNavItem(item, can)),
      };
    })
    .filter((group) =>
      group.subgroups
        ? group.subgroups.length > 0
        : (group.items || []).length > 0,
    );

// ─── SidebarItem ─────────────────────────────────────────────────────────────
// Single nav link. In collapsed mode shows only icon + tooltip.
const SidebarItem = ({ item, isActive, isCollapsed, tooltipSide = "left" }) => {
  const Icon = item.icon;

  const linkClasses = cn(
    "relative flex items-center gap-2.5 rounded-md text-[13px] transition-colors duration-150",
    isCollapsed ? "justify-center w-10 h-10 mx-auto" : "px-3 py-2 w-full",
    isActive
      ? "bg-primary-500/10 text-primary-300 font-medium"
      : "text-neutral-400 hover:bg-white/[0.06] hover:text-white",
  );

  const linkEl = (
    <Link
      to={item.path}
      data-testid={`nav-${item.path.replace(/\//g, "-").replace(/^-/, "")}`}
      className={linkClasses}
      title={
        item.spec
          ? `${item.label} — مواصفة (قراءة فقط، غير متاح — يحتاج ربط Backend)`
          : undefined
      }
    >
      {isActive && !isCollapsed && (
        <span
          aria-hidden="true"
          className="absolute end-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-s-full bg-primary-400"
        />
      )}
      {Icon && <Icon size={17} className="flex-shrink-0" />}
      {!isCollapsed && <span className="truncate">{item.label}</span>}
      {!isCollapsed && item.spec && (
        <span
          className="ms-auto flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-300 border border-amber-500/25"
          aria-label="صفحة مواصفة للقراءة فقط — غير مربوطة"
        >
          مواصفة
        </span>
      )}
      {isCollapsed && item.spec && (
        <span
          aria-hidden="true"
          className="absolute top-1 end-1 w-1.5 h-1.5 rounded-full bg-amber-400"
        />
      )}
    </Link>
  );

  if (!isCollapsed) return linkEl;

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>{linkEl}</TooltipTrigger>
        <TooltipContent side={tooltipSide} sideOffset={8}>
          {item.spec ? `${item.label} — مواصفة (قراءة فقط)` : item.label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// ─── SidebarSubGroup ──────────────────────────────────────────────────────────
// Collapsible sub-section within a group (used in HR and Platform groups).
// Persists open/close state in localStorage.
const SidebarSubGroup = ({
  subgroup,
  activePath,
  isCollapsed,
  tooltipSide = "left",
}) => {
  const hasActive = subgroup.items.some(
    (item) =>
      activePath === item.path ||
      (item.path !== "/dashboard" && activePath.startsWith(item.path)),
  );

  const lsKey = `sidebar-subgroup-${subgroup.id}`;
  const [isOpen, setIsOpen] = useState(() => readLS(lsKey, hasActive));

  // Auto-open when a child route becomes active
  useEffect(() => {
    if (hasActive && !isOpen) {
      setIsOpen(true);
      writeLS(lsKey, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePath]);

  const toggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    writeLS(lsKey, next);
  };

  // In collapsed mode, sub-groups are invisible (parent group icon handles it)
  if (isCollapsed) return null;

  return (
    <div className="mt-0.5">
      <button
        onClick={toggle}
        className={cn(
          "w-full flex items-center justify-between px-3 py-1.5 rounded-md text-[10px] font-semibold uppercase tracking-wider transition-colors duration-150",
          hasActive
            ? "text-primary-300"
            : "text-neutral-500 hover:text-neutral-300",
        )}
      >
        <span>{subgroup.label}</span>
        <ChevronDown
          size={12}
          className={cn(
            "transition-transform duration-200",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen && (
        <div className="mt-0.5 space-y-0.5 ps-3 border-s border-white/10 ms-3">
          {subgroup.items.map((item) => (
            <SidebarItem
              key={item.path}
              item={item}
              isActive={
                activePath === item.path ||
                (item.path !== "/dashboard" && activePath.startsWith(item.path))
              }
              isCollapsed={false}
              tooltipSide={tooltipSide}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── SidebarGroup ─────────────────────────────────────────────────────────────
// Top-level accordion group. Controlled by Layout so only one group is open
// at a time; Layout also derives the open group from the active route.
const SidebarGroup = ({
  group,
  activePath,
  isCollapsed,
  isOpen,
  onToggle,
  tooltipSide = "left",
}) => {
  const GroupIcon = group.icon;

  // Determine if any item inside this group is active (drives header styling).
  const hasActive = group.subgroups
    ? group.subgroups.some((sg) =>
        sg.items.some(
          (item) =>
            activePath === item.path ||
            (item.path !== "/dashboard" && activePath.startsWith(item.path)),
        ),
      )
    : (group.items || []).some(
        (item) =>
          activePath === item.path ||
          (item.path !== "/dashboard" && activePath.startsWith(item.path)),
      );

  // ── Collapsed mode: show only the group icon with a tooltip ──
  if (isCollapsed) {
    const iconEl = (
      <div
        className={cn(
          "flex items-center justify-center w-10 h-10 mx-auto rounded-md transition-colors duration-150",
          hasActive
            ? "bg-primary-500/15 text-primary-300"
            : "text-neutral-400 hover:bg-white/[0.06] hover:text-white",
        )}
      >
        <GroupIcon size={19} />
      </div>
    );

    return (
      <div className="py-0.5">
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>{iconEl}</TooltipTrigger>
            <TooltipContent side={tooltipSide} sideOffset={8}>
              {group.label}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  // ── Expanded mode: full accordion ──
  return (
    <div className="py-0.5">
      {/* Group header */}
      <button
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`sidebar-group-${group.id}-body`}
        className={cn(
          "w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-[13px] font-semibold transition-colors duration-150",
          hasActive
            ? "text-white"
            : "text-neutral-300 hover:text-white hover:bg-white/[0.04]",
        )}
      >
        <div className="flex items-center gap-2.5">
          <GroupIcon size={18} className="flex-shrink-0" />
          <span>{group.label}</span>
        </div>
        <ChevronDown
          size={14}
          className={cn(
            "flex-shrink-0 text-neutral-500 transition-transform duration-300 ease-out",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {/* Group body — max-height transition so only one group is visible at a time */}
      <div
        id={`sidebar-group-${group.id}-body`}
        className={cn(
          "overflow-hidden transition-[max-height] duration-300 ease-out motion-reduce:transition-none",
          isOpen ? "max-h-[1200px]" : "max-h-0",
        )}
      >
        <div className="mt-0.5 space-y-0.5 pb-0.5">
          {group.subgroups
            ? group.subgroups.map((sg) => (
                <SidebarSubGroup
                  key={sg.id}
                  subgroup={sg}
                  activePath={activePath}
                  isCollapsed={false}
                  tooltipSide={tooltipSide}
                />
              ))
            : (group.items || []).map((item) => (
                <SidebarItem
                  key={item.path}
                  item={item}
                  isActive={
                    activePath === item.path ||
                    (item.path !== "/dashboard" &&
                      activePath.startsWith(item.path))
                  }
                  isCollapsed={false}
                  tooltipSide={tooltipSide}
                />
              ))}
        </div>
      </div>
    </div>
  );
};

// Return the id of the top-level sidebar group that owns the given URL path.
// Mirrors the per-item `startsWith` logic used for highlighting so the open
// group always matches the highlighted item.
const findActiveGroupId = (pathname) => {
  const matches = (item) =>
    pathname === item.path ||
    (item.path !== "/dashboard" && pathname.startsWith(item.path));
  for (const group of SIDEBAR_GROUPS) {
    const hit = group.subgroups
      ? group.subgroups.some((sg) => sg.items.some(matches))
      : (group.items || []).some(matches);
    if (hit) return group.id;
  }
  return null;
};

// ─── AttendanceWidget ─────────────────────────────────────────────────────────
const AttendanceWidget = ({ user }) => {
  const [status, setStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [empId, setEmpId] = React.useState(null);

  React.useEffect(() => {
    if (!user) return;
    const fetchMyAttendance = async () => {
      try {
        const token = localStorage.getItem("token");
        const empRes = await fetch(
          `${API_URL}/hr/employees?search=${encodeURIComponent(user.email || "")}&limit=1`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!empRes.ok) return;
        const emps = await empRes.json();
        const myEmp = emps?.[0];
        if (!myEmp) return;
        setEmpId(myEmp.id);
        const todayRes = await fetch(
          `${API_URL}/hr/attendance/today/${myEmp.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!todayRes.ok) return;
        const today = await todayRes.json();
        if (today.check_in && today.check_out) setStatus("done");
        else if (today.check_in) setStatus("checked_in");
        else setStatus("not_checked_in");
      } catch {}
    };
    fetchMyAttendance();
  }, [user]);

  const handleAction = async () => {
    if (!empId || loading) return;
    setLoading(true);
    const token = localStorage.getItem("token");
    const endpoint = status === "not_checked_in" ? "check-in" : "check-out";
    try {
      let lat = null,
        lng = null;
      if (navigator.geolocation) {
        await new Promise((resolve) =>
          navigator.geolocation.getCurrentPosition(
            (p) => {
              lat = p.coords.latitude;
              lng = p.coords.longitude;
              resolve();
            },
            () => resolve(),
            { timeout: 3000 },
          ),
        );
      }
      const res = await fetch(`${API_URL}/hr/attendance/${endpoint}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ employee_id: empId, lat, lng }),
      });
      if (res.ok)
        setStatus(status === "not_checked_in" ? "checked_in" : "done");
    } catch {}
    setLoading(false);
  };

  if (!empId || status === null) return null;
  if (status === "done")
    return (
      <span className="hidden md:flex items-center gap-1 text-xs text-green-600 bg-green-50 dark:bg-green-950/30 px-2 py-1 rounded-lg">
        ✓ اكتمل يوم العمل
      </span>
    );

  return (
    <button
      onClick={handleAction}
      disabled={loading}
      className={cn(
        "hidden md:flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-colors",
        status === "not_checked_in"
          ? "bg-green-500 hover:bg-green-600 text-white"
          : "bg-red-500 hover:bg-red-600 text-white",
      )}
      title={status === "not_checked_in" ? "تسجيل الحضور" : "تسجيل الانصراف"}
    >
      {loading ? (
        "..."
      ) : status === "not_checked_in" ? (
        <>
          <LogIn className="h-3.5 w-3.5" />
          حضور
        </>
      ) : (
        <>
          <LogOutIcon className="h-3.5 w-3.5" />
          انصراف
        </>
      )}
    </button>
  );
};

// ─── Layout ───────────────────────────────────────────────────────────────────
export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { density, toggleDensity } = useDensity();
  const direction =
    typeof document !== "undefined" && document.documentElement?.dir === "ltr"
      ? "ltr"
      : "rtl";
  const isRtl = direction === "rtl";
  const tooltipSide = isRtl ? "left" : "right";
  const location = useLocation();
  const navigate = useNavigate();
  const { role, can } = usePermissions();
  const visibleSidebarGroups = filterSidebarGroups(SIDEBAR_GROUPS, can);

  // ── Sidebar collapsed state ──
  const [isCollapsed, setIsCollapsed] = useState(() =>
    readLS(LS_COLLAPSED, false),
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  // ── Sidebar accordion: exactly one top-level group open at a time. ──
  // Seeded from the current URL so the active group is already expanded on
  // first paint; a URL change re-opens whichever group owns the new route.
  const [openGroup, setOpenGroup] = useState(() =>
    findActiveGroupId(location.pathname),
  );

  useEffect(() => {
    const id = findActiveGroupId(location.pathname);
    if (id) setOpenGroup(id);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [mobileOpen]);

  const toggleGroup = useCallback((id) => {
    setOpenGroup((prev) => (prev === id ? null : id));
  }, []);

  // ── Notification state ──
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const wsRef = useRef(null);

  const toggleCollapsed = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    writeLS(LS_COLLAPSED, next);
  };

  // ── Fetch notifications ──
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/notifications?limit=10`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Company-ID": user.company_id || "",
          "X-User-ID": user.id || "",
        },
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data || []);
        setUnreadCount((data || []).filter((n) => !n.read).length);
      }
    } catch {}
  }, [user]);

  // ── WebSocket for real-time notifications ──
  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    const token = localStorage.getItem("token");
    const wsUrl = BACKEND_URL.replace(/^http/, "ws");
    const ws = new WebSocket(
      `${wsUrl}/api/notifications/ws/${user.company_id}/${user.id}?token=${token}`,
    );
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.event === "notification" && msg.data) {
          setNotifications((prev) => [msg.data, ...prev.slice(0, 9)]);
          setUnreadCount((c) => c + 1);
        }
      } catch {}
    };
    ws.onerror = () => {};
    wsRef.current = ws;
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [user, fetchNotifications]);

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_URL}/notifications/read-all`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Company-ID": user?.company_id || "",
          "X-User-ID": user?.id || "",
        },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const markOneRead = async (notif) => {
    if (!notif || notif.read) return;
    const id = notif.notification_id || notif.id || notif._id;
    if (!id) return;
    try {
      const token = localStorage.getItem("token");
      await fetch(`${API_URL}/notifications/${id}/read`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Company-ID": user?.company_id || "",
          "X-User-ID": user?.id || "",
        },
      });
    } catch {}
    setNotifications((prev) =>
      prev.map((n) =>
        (n.notification_id || n.id || n._id) === id ? { ...n, read: true } : n,
      ),
    );
    setUnreadCount((c) => Math.max(0, c - 1));
  };

  // ── Sidebar content ──
  const sidebarJsx = (
    <aside
      className={cn(
        "fixed top-0 z-40 h-screen flex flex-col",
        isRtl ? "right-0 border-l" : "left-0 border-r",
        "bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.18),transparent_28%),linear-gradient(180deg,#111827_0%,#0c0a09_100%)] dark:bg-[#0A0908]",
        "border-white/10",
        "shadow-2xl shadow-neutral-950/20 motion-reduce:transition-none transition-[width,transform] duration-200 ease-out",
        isCollapsed ? "w-16" : "w-[280px]",
        mobileOpen
          ? "translate-x-0"
          : isRtl
            ? "translate-x-full md:translate-x-0"
            : "-translate-x-full md:translate-x-0",
      )}
      data-testid="sidebar"
      aria-label="القائمة الجانبية"
      role="navigation"
    >
      {/* ── Brand / Toggle row ── */}
      <div
        className={cn(
          "flex items-center h-16 border-b border-neutral-800 flex-shrink-0",
          isCollapsed ? "justify-center px-2" : "justify-between px-4",
        )}
        data-testid="app-logo"
      >
        {!isCollapsed && (
          <TajerLogoFull height={32} className="brightness-0 invert" />
        )}
        {isCollapsed && (
          <TajerLogoIcon size={28} className="brightness-0 invert" />
        )}

        {/* Collapse toggle — desktop only */}
        <button
          onClick={toggleCollapsed}
          className={cn(
            "hidden md:flex items-center justify-center w-7 h-7 rounded-md",
            "text-neutral-400 hover:text-white",
            "hover:bg-white/10 transition-colors duration-150",
            isCollapsed && "mx-auto",
          )}
          title={isCollapsed ? "توسيع القائمة" : "طي القائمة"}
          aria-label={isCollapsed ? "توسيع القائمة" : "طي القائمة"}
        >
          {isCollapsed ? (
            <ChevronsLeft size={16} />
          ) : (
            <ChevronsRight size={16} />
          )}
        </button>
      </div>

      {/* ── Company info (expanded only) ── */}
      {!isCollapsed && user && (
        <div
          className="mx-3 mt-3 p-[var(--tp-density-gap)] bg-white/[0.04] rounded-lg border border-white/10 flex-shrink-0"
          data-testid="company-info"
        >
          <p className="text-[10px] uppercase tracking-wide text-neutral-500">
            الشركة
          </p>
          <p className="text-sm font-semibold text-white truncate mt-0.5">
            {user.company_name}
          </p>
          <div className="flex items-center justify-between mt-1.5 gap-2">
            <p className="text-xs text-neutral-400 truncate">{user.email}</p>
            <span className="text-[10px] bg-primary-500/15 text-primary-300 px-1.5 py-0.5 rounded-full whitespace-nowrap ring-1 ring-inset ring-primary-500/30">
              {ROLE_DISPLAY[role] || role}
            </span>
          </div>
        </div>
      )}

      {/* ── Navigation ── */}
      <nav
        className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5"
        aria-label="التنقل الرئيسي"
      >
        {visibleSidebarGroups.map((group) => (
          <SidebarGroup
            key={group.id}
            group={group}
            activePath={location.pathname}
            isCollapsed={isCollapsed}
            tooltipSide={tooltipSide}
            isOpen={openGroup === group.id}
            onToggle={() => toggleGroup(group.id)}
          />
        ))}
      </nav>

      {/* ── Footer: profile + logout ── */}
      <div
        className={cn(
          "flex-shrink-0 border-t border-neutral-800 py-2",
          isCollapsed ? "px-2 space-y-1" : "px-3 space-y-0.5",
        )}
      >
        {isCollapsed ? (
          <>
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to="/profile"
                    aria-label="ملفي الشخصي"
                    className="flex items-center justify-center w-10 h-10 mx-auto rounded-md text-neutral-400 hover:bg-white/[0.06] hover:text-white transition-colors duration-150"
                  >
                    <UserCircle size={19} />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side={tooltipSide} sideOffset={8}>
                  ملفي الشخصي
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => {
                      logout();
                      navigate("/login");
                    }}
                    aria-label="تسجيل الخروج"
                    className="flex items-center justify-center w-10 h-10 mx-auto rounded-md text-neutral-400 hover:bg-danger-500/15 hover:text-danger-300 transition-colors duration-150"
                    data-testid="logout-button"
                  >
                    <LogOut size={19} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side={tooltipSide} sideOffset={8}>
                  تسجيل الخروج
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        ) : (
          <>
            <Link
              to="/profile"
              className="flex items-center gap-3 px-3 py-2 rounded-md text-[13px] text-neutral-300 hover:bg-white/[0.06] hover:text-white transition-colors duration-150"
            >
              <UserCircle size={17} />
              <span>ملفي الشخصي</span>
            </Link>
            <button
              onClick={() => {
                logout();
                navigate("/login");
              }}
              data-testid="logout-button"
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-[13px] text-neutral-300 hover:bg-danger-500/15 hover:text-danger-300 transition-colors duration-150"
            >
              <LogOut size={17} />
              <span>تسجيل الخروج</span>
            </button>
          </>
        )}
      </div>
    </aside>
  );

  return (
    <div
      className="min-h-screen bg-[var(--tp-color-bg)] dark:bg-background"
      dir={direction}
      data-density={density}
      data-testid="app-shell"
    >
      {/* ── Sidebar ── */}
      {sidebarJsx}

      {/* Mobile overlay — decorative backdrop; close action mirrored on the X button. */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Main area (shifts based on sidebar width) ── */}
      <div
        className={cn(
          "flex flex-col min-h-screen motion-reduce:transition-none transition-[margin] duration-200 ease-out",
          isRtl
            ? isCollapsed
              ? "md:mr-16"
              : "md:mr-[280px]"
            : isCollapsed
              ? "md:ml-16"
              : "md:ml-[280px]",
        )}
      >
        {/* ── Header ── */}
        <header
          className="sticky top-0 z-20 flex min-h-[var(--tp-density-row-height)] items-center border-b border-[var(--tp-color-border)] bg-white/92 shadow-sm shadow-neutral-900/5 backdrop-blur-xl dark:border-neutral-800/80 dark:bg-neutral-950/85"
          data-testid="main-header"
        >
          <div className="w-full px-[var(--tp-density-gap)] md:px-6 flex items-center justify-between gap-[var(--tp-density-gap)]">
            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md transition-colors"
              data-testid="menu-toggle"
              aria-label={mobileOpen ? "إغلاق القائمة" : "فتح القائمة"}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>

            {/* Global Search */}
            <GlobalSearch />

            {/* Right side controls */}
            <div className="flex items-center gap-1.5 mr-auto">
              <BranchSwitcher />
              <AttendanceWidget user={user} />
              <ThemeToggle />

              <button
                type="button"
                onClick={toggleDensity}
                className="tp-control min-w-11 px-2 text-xs font-medium text-neutral-600 hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:text-neutral-300 dark:hover:bg-neutral-800"
                aria-label={
                  density === "compact"
                    ? "تفعيل العرض المريح"
                    : "تفعيل العرض المضغوط"
                }
                data-testid="density-toggle"
              >
                {density === "compact" ? "مريح" : "مضغوط"}
              </button>

              {/* Notification center */}
              <NotificationCenter
                open={notifOpen}
                notifications={notifications}
                unreadCount={unreadCount}
                onToggle={() => {
                  setNotifOpen(!notifOpen);
                  if (!notifOpen) fetchNotifications();
                }}
                onClose={() => setNotifOpen(false)}
                onMarkAllRead={markAllRead}
                onNotificationClick={(n) => {
                  markOneRead(n);
                  // react-router forwards absolute cross-origin URLs to
                  // window.location — only follow trusted action URLs.
                  if (n.action_url && isSafeUrl(n.action_url))
                    navigate(n.action_url);
                  setNotifOpen(false);
                }}
              />

              {/* User avatar */}
              <Link
                to="/profile"
                aria-label={
                  user?.full_name
                    ? `الملف الشخصي — ${user.full_name}`
                    : "الملف الشخصي"
                }
                className="flex items-center gap-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md px-1.5 py-1 transition-colors"
              >
                <div className="text-start hidden md:block pe-1">
                  <p className="text-[13px] font-semibold text-neutral-800 dark:text-neutral-100 leading-tight">
                    {user?.full_name}
                  </p>
                  <p className="text-[11px] text-neutral-500 dark:text-neutral-400 leading-tight mt-0.5">
                    {ROLE_DISPLAY[role] || role}
                  </p>
                </div>
                <div
                  className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white text-sm font-bold shadow-sm"
                  aria-hidden="true"
                >
                  {user?.full_name?.charAt(0) || "?"}
                </div>
              </Link>
            </div>
          </div>
        </header>

        {/* ── Page content ── */}
        <main
          id="main-content"
          className="flex-1 overflow-y-auto bg-[linear-gradient(180deg,var(--tp-color-bg)_0%,#f5f5f4_100%)] animate-fade-in dark:bg-[linear-gradient(180deg,rgba(10,10,10,1)_0%,rgba(12,10,9,1)_100%)]"
          data-testid="main-content"
        >
          <div className="mx-auto max-w-[1440px] px-[var(--tp-density-gap)] py-[calc(var(--tp-density-gap)*1.5)] md:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>

      {/* Click-outside to close notifications */}
      {notifOpen && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setNotifOpen(false)}
        />
      )}

      <FloatingActionButton />
    </div>
  );
};
